import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { DiscussionListItem, DiscussionType } from '../../models/discussion.models';
import {
  CreateDiscussionEffectsActions,
  DiscussionDetailActions,
  DiscussionListActions,
  DiscussionListEffectActions,
  DiscussionToolbarComponentActions,
} from '../actions';

export interface ListState extends EntityState<DiscussionListItem> {
  totalDiscussions: number;
  loadingDiscussionList: boolean;
  allDiscussionsLoaded: boolean;
  pager: {
    page: number;
    take: number;
  };
  sort: {
    sortField: keyof Pick<DiscussionListItem, 'dueDate'>;
    sortDirection: 'ASC' | 'DESC';
  };
  filter: {
    type: DiscussionType.quarterly;
    selectedUserId: string;
    showCompleted: boolean;
  };
}

const adapter: EntityAdapter<DiscussionListItem> = createEntityAdapter<DiscussionListItem>({
  selectId: d => d._id,
});

export const InitialListState: ListState = adapter.getInitialState({
  totalDiscussions: 0,
  loadingDiscussionList: false,
  allDiscussionsLoaded: false,
  pager: {
    page: 0,
    take: 10,
  },
  sort: {
    sortField: 'dueDate',
    sortDirection: 'DESC',
  },
  filter: {
    type: DiscussionType.quarterly,
    selectedUserId: 'all',
    showCompleted: false,
  },
});

export const listReducer = createReducer<ListState>(
  InitialListState,
  on(DiscussionListActions.discussionListInit, (state): ListState => ({ ...state, loadingDiscussionList: true })),
  on(DiscussionListActions.discussionRefresh, (state): ListState => ({
    ...state,
    ...adapter.removeAll(state),
    allDiscussionsLoaded: false,
    loadingDiscussionList: true,
    pager: { ...InitialListState.pager },
  })),
  on(
    DiscussionListEffectActions.discussionListRequestSucceeded,
    (state, { data, totalDiscussions, event }): ListState => ({
      ...adapter.addMany(data, state),
      totalDiscussions,
      loadingDiscussionList: false,
      allDiscussionsLoaded: data.length < state.pager.take,
      pager: event ? { ...state.pager, page: state.pager.page + 1 } : { ...state.pager },
    })
  ),
  on(DiscussionListEffectActions.discussionListRequestFailed, (state): ListState => ({
    ...state,
    loadingDiscussionList: false,
  })),
  on(DiscussionListEffectActions.discussionListInit, (state, { currentUserId }): ListState => ({
    ...state,
    filter: {
      ...state.filter,
      selectedUserId: currentUserId,
    },
  })),
  on(
    DiscussionDetailActions.discussionDetailSaved,
    DiscussionDetailActions.discussionDetailUpdateReverted,
    (state, { update }): ListState => adapter.updateOne(update, state)
  ),
  on(DiscussionDetailActions.discussionDetailDeleted, (state, { id }): ListState => adapter.removeOne(id, state)),
  on(DiscussionDetailActions.discussionDetailDeleteFailed, (state, { discussion }): ListState =>
    adapter.addOne(discussion, state)
  ),
  on(DiscussionToolbarComponentActions.sortDirectionChanged, (state): ListState => ({
    ...adapter.removeAll(state),
    allDiscussionsLoaded: false,
    loadingDiscussionList: true,
    sort: {
      ...state.sort,
      sortDirection: state.sort.sortDirection === 'ASC' ? 'DESC' : 'ASC',
    },
    pager: { ...InitialListState.pager },
  })),
  on(DiscussionToolbarComponentActions.listFiltered, (state, { filters }): ListState => ({
    ...adapter.removeAll(state),
    allDiscussionsLoaded: false,
    loadingDiscussionList: true,
    pager: { ...InitialListState.pager },
    filter: { ...state.filter, ...filters },
  })),
  on(CreateDiscussionEffectsActions.createDiscussionSucceeded, (state, { discussion }): ListState => {
    const currentDiscussions = state.ids.map(id => state.entities?.[id]).filter(d => !!d);
    return adapter.setAll([discussion, ...currentDiscussions], state);
  })
);

export const { selectAll, selectTotal, selectIds, selectEntities } = adapter.getSelectors();
